import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, PopupInnerComponentClass, Service } from 'webapp-types';
import type { AttachPosition } from 'webapp/enums/enums';
import { Direction, PopupEvent } from 'webapp/enums/enums';

import type { Args as SelectArgs } from 'webapp/components/form/select';
import FormSelectComponent from 'webapp/components/form/select';

type Args = SelectArgs & {
	popupComponent: PopupInnerComponentClass;
	popupClass?: string;
	popupDirections?: Direction[];
	popupOffset?: number;
	popupInheritWidth?: boolean;
	popupAllowStacked?: boolean;
	popupAllowMultipleInstances?: boolean;
	popupHasOverlay?: boolean;
	popupCloseOnOverlayClick?: boolean;
	popupSkipAttachToCheck?: boolean;
	popupAttachPosition?: AttachPosition;
	popupalignEdgeWithAttachTo?: boolean;
	popupSkipDropdownClasses?: boolean;
	getPopupProps?: () => object; // the popup data
};

export default class FormSelectWithPopupAsDropdownComponent extends FormSelectComponent<Args> {
	@service('popup') declare popupService: Service.Popup;

	// ------ for select dropdown ----- //

	override get tooltipOnSelectionFromProperty() {
		// canceling default
		return this.args.tooltipOnSelectionFromProperty ?? '';
	}

	override get extraClass() {
		// for the select
		const classes = (super.extraClass || '').split(' ');

		classes.push('custom-select--with-popup');
		classes.push(this.popupDirectionClass);
		if (this.isPopupOpen) {
			classes.push('isOpen');
		}
		if (this.args.extraClass) {
			classes.push(this.args.extraClass);
		}

		return classes.join(' ');
	}

	// ------ for popup ----- //

	@tracked isPopupOpen = false;
	@tracked currentPopupDirection: Direction | null = null;

	get popupOffset() {
		if (typeof this.args.popupOffset === 'number') {
			return this.args.popupOffset;
		} else {
			// offset it so it overlaps the dropdown itself
			let offset = -1;
			offset -= this.size === 'small' ? 34 : 41;
			return offset;
		}
	}

	get popupDirections() {
		return this.args.popupDirections || [Direction.DOWN, Direction.UP];
	}

	get popupDirectionClass() {
		const popupDirection = this.currentPopupDirection;
		if (popupDirection) {
			return `popup-open--${popupDirection}`;
		} else {
			return '';
		}
	}

	get popupInheritWidth() {
		return this.args.popupInheritWidth ?? true;
	}

	get popupAllowStacked() {
		return this.args.popupAllowStacked ?? false;
	}

	get popupAllowMultipleInstances() {
		return this.args.popupAllowMultipleInstances ?? false;
	}

	get popupHasOverlay() {
		return this.args.popupHasOverlay ?? false;
	}

	get popupCloseOnOverlayClick() {
		return this.args.popupCloseOnOverlayClick ?? false;
	}

	getAttachTo(selectElement: JQuery<HTMLElement>): HTMLElement {
		let attachTo = selectElement.next('.select2');
		if (!attachTo.length) {
			// because this element might be wrapped in a floatlabel-wrapper
			attachTo = selectElement.parent().next('.select2');
		}

		return attachTo.get(0)!;
	}

	openPopup() {
		const el = this._selectElement;

		if (this.isPopupOpen || !el) {
			return;
		}

		if (
			this.popupService.isOpen(this.args.popupComponent) &&
			!this.popupAllowMultipleInstances
		) {
			return;
		}

		const attachToNode = this.getAttachTo(el);

		let popupClass = this.args.popupClass || '';
		if (!this.args.popupSkipDropdownClasses) {
			popupClass += ' popup-as-dropdown popup-from-dropdown';
		}

		const props = this.args.getPopupProps?.() || {};
		const popup = this.popupService.createContext(this.args.popupComponent, attachToNode, {
			offset: this.popupOffset,
			directions: this.popupDirections,
			popupClass,
			theme: this.theme,
			inheritWidth: this.popupInheritWidth,
			allowStacked: this.popupAllowStacked,
			allowMultipleInstances: this.popupAllowMultipleInstances,
			hasOverlay: this.popupHasOverlay,
			closeOnOverlayClick: this.popupCloseOnOverlayClick,
			skipAttachToCheck: this.args.popupSkipAttachToCheck,
			attachPosition: this.args.popupAttachPosition,
			alignEdgeWithAttachTo: this.args.popupalignEdgeWithAttachTo,
			onChangeDirection: (direction: Direction) => {
				this.currentPopupDirection = direction;
			},
			hideArrow: true,
		});

		popup.on(PopupEvent.OPENING, this, () => {
			this.isPopupOpen = true;
		});

		popup.open(props as PopupData.Any); // the popup component is passed in

		popup.on(PopupEvent.CLOSING, this, () => {
			this.isPopupOpen = false;
		});

		return popup;
	}

	override get onOpening() {
		return () => {
			const openPopup = this.popupService.findByComponent(this.args.popupComponent);
			if (openPopup?.isOpen || openPopup?.isOpening) {
				void openPopup.close();
				return;
			}

			const popup = this.openPopup();

			if (popup) {
				this.popupService.blockCloseOnOutsideClick = true;

				later(() => {
					this.popupService.blockCloseOnOutsideClick = false;
				}, 500);
			}
		};
	}
}
